import React, { CSSProperties } from 'react';

import ProgressBadge from 'components/FinancialCheckup/FinancialDashboard/GoalsSection/GoalProgressCircle/ProgressBadge';
import {
  calculatePathOffset,
  useSVGPathLength,
  SVG_VIEWBOX_HEIGHT,
  SVG_VIEWBOX_WIDTH,
} from 'components/FinancialCheckup/FinancialDashboard/GoalsSection/GoalProgressCircle/utils/svgAnimationUtils';

import { getCreditScoreFillColor, getCreditScorePathFillPercentage, CREDIT_SCORE_PATHS } from './creditScoreUtils';

import styles from './CreditScoreProgressCircle.module.scss';

interface CreditScoreProgressCircleProps {
  value: number;
}

const CreditScoreProgressCircle: React.FC<CreditScoreProgressCircleProps> = ({ value }) => {
  const badgeColor = getCreditScoreFillColor(value);
  const { pathRefs, pathLengths } = useSVGPathLength(CREDIT_SCORE_PATHS.length);

  const getCreditScorePathOffset = (pathIndex: number): number => {
    const fillPercentage = getCreditScorePathFillPercentage(pathIndex, value);
    return calculatePathOffset({
      pathLength: pathLengths[pathIndex] || 0,
      fillPercentage,
    });
  };

  return (
    <svg
      width={SVG_VIEWBOX_WIDTH}
      height={SVG_VIEWBOX_HEIGHT}
      viewBox={`0 0 ${SVG_VIEWBOX_WIDTH} ${SVG_VIEWBOX_HEIGHT}`}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      {CREDIT_SCORE_PATHS.map((path, i) => {
        const currentPathLength = pathLengths[i] || 0;
        const pathOffset = getCreditScorePathOffset(i);

        return (
          <React.Fragment key={i}>
            <g filter="url(#filter_credit_score)">
              <path
                ref={(el) => {
                  pathRefs.current[i] = el;
                }}
                d={path.path}
                strokeWidth="12"
                strokeLinejoin="round"
                shapeRendering="crispEdges"
                className={styles.outline}
              />
            </g>
            <path
              d={path.path}
              className={styles.fill}
              style={{ '--fill-color': path.stroke } as CSSProperties}
              strokeWidth="12"
              strokeLinecap="round"
              strokeLinejoin="round"
              shapeRendering="crispEdges"
              strokeDasharray={currentPathLength}
              strokeDashoffset={pathOffset}
            />
          </React.Fragment>
        );
      })}
      <ProgressBadge badgeColor={badgeColor} />
      <defs>
        <filter
          id="filter_credit_score"
          x="0"
          y="0"
          width={SVG_VIEWBOX_WIDTH}
          height={SVG_VIEWBOX_HEIGHT}
          filterUnits="userSpaceOnUse"
          colorInterpolationFilters="sRGB"
        >
          <feFlood floodOpacity="0" result="BackgroundImageFix" />
          <feColorMatrix
            in="SourceAlpha"
            type="matrix"
            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
            result="hardAlpha"
          />
          <feGaussianBlur stdDeviation="1" />
          <feComposite in2="hardAlpha" operator="out" />
          <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.25 0" />
          <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_filter_credit_score" />
          <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_filter_credit_score" result="shape" />
        </filter>
      </defs>
    </svg>
  );
};

export default CreditScoreProgressCircle;
